import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth, usePermission, getRoleLabel, getRoleColor } from '../context/AuthContext';
import sgLogoWide from '../assets/logo/sg-logo-wide.png';
import './Layout.css';

/**
 * Base layout properties
 */
export interface LayoutProps {
  children: React.ReactNode;
  header?: React.ReactNode;
  sidebar?: React.ReactNode;
}

/**
 * Base Layout Component
 */
export const Layout: React.FC<LayoutProps> = ({
  children,
  header,
  sidebar,
}) => {
  return (
    <div className="layout">
      {header && <header className="layout-header">{header}</header>}
      <div className="layout-container">
        {sidebar && <aside className="layout-sidebar">{sidebar}</aside>}
        <main className="layout-main">{children}</main>
      </div>
    </div>
  );
};

/**
 * Admin Header Component
 */
const AdminHeader: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const canViewSettings = usePermission('settings.view');

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <header className="admin-header">
      <div className="admin-header-inner">
        {/* Logo */}
        <Link to="/admin/dashboard" className="admin-header-logo">
          <img src={sgLogoWide} alt="Seraphina Grey" />
        </Link>

        {/* Navigation */}
        <nav className="admin-nav">
          <Link
            to="/admin/dashboard"
            className={`admin-nav-link ${isActive('/admin/dashboard') ? 'active' : ''}`}
          >
            Dashboard
          </Link>
          <Link
            to="/admin/sessions/new"
            className={`admin-nav-link ${isActive('/admin/sessions/new') ? 'active' : ''}`}
          >
            New Session
          </Link>
          {canViewSettings && (
            <Link
              to="/admin/settings"
              className={`admin-nav-link ${isActive('/admin/settings') ? 'active' : ''}`}
            >
              Settings
            </Link>
          )}
        </nav>

        {/* User menu */}
        <div className="admin-user-menu">
          <div className="admin-user-info">
            <span className="admin-user-name">
              {user?.display_name || user?.username}
            </span>
            {user?.role && (
              <span className={`admin-role-badge ${getRoleColor(user.role)}`}>
                {getRoleLabel(user.role)}
              </span>
            )}
          </div>
          <button onClick={handleLogout} className="admin-logout-btn">
            Sign Out
          </button>
        </div>
      </div>
    </header>
  );
};

/**
 * Admin Layout Component
 *
 * Full admin portal layout with header and main content area.
 * Used for all /admin/* routes.
 */
export const AdminLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div className="admin-layout">
      <AdminHeader />
      <main className="admin-main">
        <div className="admin-content">
          {children}
        </div>
      </main>
    </div>
  );
};

/**
 * Customer Layout Component
 *
 * Simplified layout for customer-facing proof review pages.
 * Used for /proof/* routes.
 */
export const CustomerLayout: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  return (
    <div className="customer-layout">
      <header className="customer-header">
        <img src={sgLogoWide} alt="Seraphina Grey" className="customer-logo" />
      </header>
      <main className="customer-main">
        {children}
      </main>
    </div>
  );
};
